// useTableData.js — owns query state + the fetch effect for DataTable.
import { useState, useEffect, useRef, useCallback } from "react";
import useFetchDatav2 from "../useFetchDatav2";
import { sliceClientRows } from "./clientAdapter";

export default function useTableData({ config, adapter, clientRows }) {
   const isClient = adapter?.mode === "client";
   const { fetchData } = useFetchDatav2();
   const [rows, setRows] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10, totalPages: 0, totalRows: null });
   const [filters, setFilters] = useState({});
   const [search, setSearch] = useState("");
   const [sort, setSort] = useState(config.defaultSort || null);
   const [meta, setMeta] = useState({ stateCounts: null });
   const [quickFilter, setQuickFilterState] = useState(config.quickFilter?.defaultValue ?? null);

   const abortRef = useRef(null);
   const prevKeyRef = useRef(null);

   useEffect(() => {
      if (isClient) return;
      const key = JSON.stringify({
         pageIndex: pagination.pageIndex,
         pageSize: pagination.pageSize,
         sort,
         filters,
         search,
         quickFilter,
      });
      if (prevKeyRef.current === key) return;
      prevKeyRef.current = key;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const run = async () => {
         setLoading(true);
         const fd = adapter.buildFormData({
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            sort,
            filters,
            search,
            quickFilter,
         });
         const { responseData, fetchError } = await fetchData(adapter.endpoint, "POST", fd, "form-data", "json", controller.signal);
         if (fetchError) {
            setLoading(false);
            return; // aborted or network error; useFetchDatav2 handles session expiry
         }
         const parsed = adapter.parseResponse(responseData);
         if (!parsed.ok) {
            setError(parsed.message);
            setRows([]);
            setLoading(false);
            return;
         }
         setError("");
         setRows(parsed.rows);
         setMeta({ stateCounts: parsed.stateCounts });
         setPagination((prev) => (
            prev.totalPages === parsed.totalPages && prev.totalRows === parsed.totalRows
               ? prev
               : { ...prev, totalPages: parsed.totalPages, totalRows: parsed.totalRows }
         ));
         setLoading(false);
      };
      run();
      // adapter + fetchData are stable for our usage; excluded to avoid refetch loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [pagination.pageIndex, pagination.pageSize, sort, filters, search, quickFilter]);

   // Client mode: derive the visible page from the in-memory array the host passes.
   useEffect(() => {
      if (!isClient) return;
      const { pageRows, totalRows, totalPages, pageIndex } = sliceClientRows({
         rows: clientRows,
         sort,
         columns: config.columns,
         pageIndex: pagination.pageIndex,
         pageSize: pagination.pageSize,
      });
      setRows(pageRows);
      setError("");
      setMeta({ stateCounts: null });
      setLoading(false);
      setPagination((prev) =>
         prev.totalPages === totalPages && prev.totalRows === totalRows && prev.pageIndex === pageIndex
            ? prev
            : { ...prev, totalPages, totalRows, pageIndex }
      );
   }, [isClient, clientRows, sort, pagination.pageIndex, pagination.pageSize, config.columns]);

   // Client mode: when the underlying (already-filtered) array changes, go back to page 1.
   useEffect(() => {
      if (!isClient) return;
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
   }, [isClient, clientRows]);

   useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

   const setFilter = useCallback((field, value) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
   }, []);
   const setQuickFilter = useCallback((value) => {
      setQuickFilterState(value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
   }, []);
   const clearFilters = useCallback(() => {
      setFilters({});
      setSearch("");
      setQuickFilterState(config.quickFilter?.defaultValue ?? null);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
   }, [config.quickFilter?.defaultValue]);
   const changeSearch = useCallback((value) => {
      setSearch(value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
   }, []);
   const toggleSort = useCallback((field) => {
      setSort((prev) => (!prev || prev.field !== field ? { field, dir: "asc" } : { field, dir: prev.dir === "asc" ? "desc" : "asc" }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
   }, []);
   const nextPage = useCallback(() => setPagination((p) => ({ ...p, pageIndex: Math.min(p.pageIndex + 1, p.totalPages - 1) })), []);
   const prevPage = useCallback(() => setPagination((p) => ({ ...p, pageIndex: Math.max(p.pageIndex - 1, 0) })), []);
   const setPageSize = useCallback((size) => setPagination((p) => ({ ...p, pageSize: size, pageIndex: 0 })), []);

   return {
      rows, loading, error, pagination, filters, search, sort, meta, quickFilter,
      setFilter, clearFilters, changeSearch, toggleSort, nextPage, prevPage, setPageSize, setQuickFilter,
   };
}
